<template>
  <section class="hp-section hp-section--compact hp-posts hp-animate" v-if="loading || posts.length">
    <div class="container">
      <div class="hp-section__head">
        <div>
          <h2 class="hp-section__title">Featured challenges</h2>
          <p class="hp-section__subtitle">Practice tests &amp; hiring challenges you can attempt now</p>
        </div>
      </div>

      <div v-if="posts.length && !loading" class="hp-posts__grid">
        <a
          v-for="post in posts"
          :key="post.id || post.post_id"
          :href="getPostLink(post)"
          class="hp-post-card"
          :class="'hp-post-card--' + getPostCategory(post).slug"
          target="_blank"
        >
          <div v-if="getPostImage(post)" class="hp-post-card__thumb">
            <img :src="getPostImage(post)" :alt="getPostTitle(post)" loading="lazy" />
          </div>
          <div class="hp-post-card__inner">
            <div class="hp-post-card__meta">
              <span class="hp-post-card__tag">{{ getPostCategory(post).label }}</span>
              <span v-if="isRecent(post)" class="hp-post-card__tag hp-post-card__tag--new">New</span>
              <span class="hp-post-card__read">{{ getDuration(post) }}</span>
            </div>
            <h3 class="hp-post-card__title">{{ getPostTitle(post) }}</h3>
            <p class="hp-post-card__desc">{{ getPostExcerpt(post) }}</p>
            <div class="hp-post-card__footer">
              <span class="hp-post-card__cta">{{ getCtaLabel(post) }} <span aria-hidden="true">→</span></span>
            </div>
          </div>
        </a>
      </div>

      <div v-if="loading" class="hp-posts__grid">
        <div class="hp-post-card hp-post-card--skeleton" v-for="n in 3" :key="n">
          <content-loader viewBox="0 0 320 160" primaryColor="#f1f5f9" secondaryColor="#e2e8f0">
            <rect x="0" y="0" rx="4" ry="4" width="80" height="10" />
            <rect x="0" y="24" rx="4" ry="4" width="260" height="16" />
            <rect x="0" y="48" rx="4" ry="4" width="280" height="10" />
            <rect x="0" y="66" rx="4" ry="4" width="240" height="10" />
            <rect x="0" y="130" rx="4" ry="4" width="100" height="10" />
          </content-loader>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { ContentLoader } from 'vue-content-loader';
import { FEATURED_CHALLENGE_EXCERPTS } from '@/constants/homePageData';
import { formatPostDate, isRecentPost } from '@/utils/postHelpers';

const CATEGORY_RULES = [
  { match: /challenge|sell|sales/i, label: 'Challenge', slug: 'challenge', cta: 'Start challenge' },
  { match: /test|assessment|employability|aptitude|quiz/i, label: 'Assessment', slug: 'assessment', cta: 'Take test' },
  { match: /interview|tips|question/i, label: 'Interview prep', slug: 'interview', cta: 'Attempt now' },
  { match: /skill|learn|course|upskill/i, label: 'Skills', slug: 'skills', cta: 'Attempt now' },
];

const DEFAULT_CATEGORY = { label: 'Challenge', slug: 'challenge', cta: 'Attempt now' };

export default {
  name: 'HomeFeaturedPosts',
  components: { ContentLoader },
  props: {
    posts: { type: Array, default: () => [] },
    loading: { type: Boolean, default: true },
  },
  methods: {
    getPostTitle(post) {
      return post.title || post.post_title || post.name || 'Challenge';
    },
    getPostExcerpt(post) {
      const title = this.getPostTitle(post);
      const mapped = FEATURED_CHALLENGE_EXCERPTS.find(r => r.match.test(title));
      if (mapped) {
        return mapped.excerpt;
      }
      const desc = this.stripHtml(
        post.short_description || post.description || post.summary || ''
      );
      if (desc) {
        return desc.length > 140 ? desc.slice(0, 140) + '…' : desc;
      }
      return 'Attempt this challenge and see how you score against other candidates.';
    },
    stripHtml(text) {
      return (text || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    },
    formatPostDate,
    getPostLink(post) {
      const id = post.id || post.post_id;
      const slug = this.slugify(this.getPostTitle(post));
      if (!id) return '/posts';
      return '/post/' + slug + '-' + id;
    },
    getPostImage(post) {
      const raw = post.image_url || post.banner_url || post.thumbnail
        || post.post_image || post.featured_image || post.image || '';
      if (!raw) return '';
      if (raw.match(/^https?:\/\//i)) return raw;
      return (this.$S3_url || '') + raw;
    },
    getPostCategory(post) {
      const text = this.getPostTitle(post) + ' ' + this.stripHtml(
        post.short_description || post.description || post.summary || ''
      );
      return CATEGORY_RULES.find(r => r.match.test(text)) || DEFAULT_CATEGORY;
    },
    getCtaLabel(post) {
      return this.getPostCategory(post).cta || DEFAULT_CATEGORY.cta;
    },
    getDuration(post) {
      const title = this.getPostTitle(post);
      const fromTitle = title.match(/(\d+)\s*[- ]?\s*(?:min(?:ute)?s?)/i);
      if (fromTitle) {
        return fromTitle[1] + ' min';
      }
      const fromDesc = this.stripHtml(
        post.short_description || post.description || post.summary || ''
      ).match(/(\d+)\s*[- ]?\s*(?:min(?:ute)?s?)/i);
      if (fromDesc) {
        return fromDesc[1] + ' min';
      }
      return 'Timed test';
    },
    isRecent(post) {
      return isRecentPost(post);
    },
    slugify(text) {
      return (text || '').toString().toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '');
    },
  },
};
</script>
